import type { DashboardSummary } from './dashboard.api';

// Dashboard summary cache at MODULE scope (deliberately NOT a component-instance
// ref). App.tsx is a state-based router that fully unmounts the DashboardPage
// subtree when navigating to a game replay / Terms / Privacy / FAQ and back; an
// instance ref would be destroyed on unmount, so the remount would flash the
// skeleton and refetch. A module cache survives that round-trip.
//
// Keyed by user so a logout + different login can't surface the previous
// account's data. The 30s TTL governs the *background* refresh only — the
// initial seed in DashboardPage ignores it (stale-while-revalidate) so returning
// always renders full data immediately (no skeleton, no layout jump).
//
// This lives in its own module (not inside DashboardPage.tsx) so App.tsx can
// import `clearDashboardCache` on logout without statically pulling the lazy
// DashboardPage chunk into the main bundle. The `DashboardSummary` import is
// type-only and erased at build time.
export const DASHBOARD_CACHE_TTL_MS = 30_000;

export const dashboardCache = new Map<string, { data: DashboardSummary; ts: number }>();

export function dashboardCacheKey(userId: string, variant: string, timeControl: string): string {
  return `${userId}:${variant}:${timeControl}`;
}

/** Clear the cache (call on logout to avoid a cross-account data leak). */
export function clearDashboardCache(): void {
  dashboardCache.clear();
}

type StaleListener = () => void;
const staleListeners = new Set<StaleListener>();

/**
 * Signal that the user just did something the dashboard summarises — finished a
 * game, solved a puzzle — so the streak / daily goal / heatmap / recent games
 * are out of date.
 *
 * Entries are marked stale (ts: 0) rather than deleted: a mounted DashboardPage
 * keeps rendering the previous numbers while it refetches, and a remount still
 * seeds from cache. Deleting them would bring back the skeleton flash this
 * module exists to prevent.
 *
 * The listener set matters on mobile, where the puzzle opens as an overlay on
 * top of the dashboard (App.tsx): DashboardPage never unmounts and no
 * focus/visibilitychange ever fires, so without a push signal the card would sit
 * on stale data until the 30s TTL lapsed AND something re-ran the fetch effect.
 */
export function markDashboardStale(): void {
  for (const [key, entry] of dashboardCache) {
    dashboardCache.set(key, { data: entry.data, ts: 0 });
  }
  for (const listener of staleListeners) listener();
}

/** Subscribe a mounted DashboardPage to stale signals. Returns an unsubscribe. */
export function subscribeDashboardStale(listener: StaleListener): () => void {
  staleListeners.add(listener);
  return () => {
    staleListeners.delete(listener);
  };
}

/** Test-only: reset the module cache between cases. */
export function __resetDashboardCache(): void {
  dashboardCache.clear();
  staleListeners.clear();
}
